import axios from 'axios';
import { FormEvent, useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { HubSpotLink, NodeServer } from '../config';
import '../styles/UserProfile.scss';
import Loading from './Loading';
import ProfileCard from './ProfileCard';

interface Profile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  jobTitle: string;
  phoneNumber: string;
  nftId: string;
  image: string;
  name: string;
  occupationName: string;
  collection: string;
}

function UserProfile(props: { title: string }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    jobTitle: '',
    phoneNumber: '',
    nftId: '',
  } as Profile);

  const [profileCard, setProfileCard] = useState({
    image: '',
    name: '',
    occupationName: '',
    collection: '',
  });

  const [loading, setLoading] = useState(true);
  const [saveChanges, setSaveChanges] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [formData, setFormData] = useState({
    fields: {
      firstname: '',
      lastname: '',
      email: '',
      company: '',
      jobtitle: '',
      phone: '',
      message: '',
      updates_and_offers: false,
    },
    errors: {
      company: false,
      jobtitle: false,
      message: false,
    },
  });

  const onAvatarClick = () => {
    if (profile.image) {
      navigate(`/avatar?id=${profile.nftId}`);
    }
  };

  const handleChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.currentTarget;
    if (name === 'company' || name === 'jobtitle' || name === 'phone') {
      setSaveChanges(true);
    }

    setFormData({
      ...formData,
      fields: {
        ...formData.fields,
        [name]:
          event.currentTarget.type === 'checkbox'
            ? (event.currentTarget as HTMLInputElement).checked
            : value,
      },
    });
  };

  const editProfile = async () => {
    setLoading(true);
    setSaveChanges(false);
    const { data } = (await axios.patch(
      `${NodeServer.baseURL}${NodeServer.userProfile}`,
      {
        companyName: formData.fields.company,
        jobTitle: formData.fields.jobtitle,
        phoneNumber: formData.fields.phone,
      },
      { withCredentials: true }
    )) as { data: Profile };
    setProfile({
      ...profile,
      ...data,
    });
    setLoading(false);
  };

  const handleValidation = () => {
    let fields = formData.fields;
    let errors = {
      company: false,
      jobtitle: false,
      message: false,
    };
    const formIsValid =
      fields['company'] && fields['jobtitle'] && fields['message'];

    errors['company'] = !fields['company'];
    errors['jobtitle'] = !fields['jobtitle'];
    errors['message'] = !fields['message'];

    setFormData({
      ...formData,
      errors,
    });
    return formIsValid;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (handleValidation()) {
      const fields = (
        Object.keys(formData.fields) as Array<keyof typeof formData.fields>
      ).map((key) => ({
        name: key,
        value: formData.fields[key] || '',
      }));

      const hubspotResponse = await axios.post(HubSpotLink, {
        fields,
      });

      if (hubspotResponse.data.inlineMessage) {
        setMessageSent(true);
      }
    }
  };

  useEffect(() => {
    const getUserProfile = async () => {
      const response = await axios.get<Profile>(
        `${NodeServer.baseURL}${NodeServer.userProfile}`,
        { withCredentials: true }
      );
      setProfile(response.data as Profile);
      const { image, name, occupationName, collection } = response.data;
      setProfileCard({
        image,
        name,
        occupationName,
        collection,
      });
      setFormData({
        fields: {
          firstname: response.data.firstName,
          lastname: response.data.lastName,
          email: response.data.email,
          company: response.data.companyName,
          jobtitle: response.data.jobTitle,
          phone: response.data.phoneNumber,
          message: '',
          updates_and_offers: false,
        },
        errors: {
          company: false,
          jobtitle: false,
          message: false,
        },
      });
      setLoading(false);
    };
    getUserProfile();
  }, []);

  useEffect(() => {
    document.title = props.title;
  });

  return loading ? (
    <div className="loading-container">
      <Loading />
    </div>
  ) : (
    <div className="user-profile-page">
      {profile && (
        <div className="profile-info">
          <h1>WELCOME</h1>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <label>
                FIRST NAME
                <input
                  type="text"
                  name="firstname"
                  defaultValue={profile.firstName}
                  disabled
                />
              </label>

              <label>
                LAST NAME
                <input
                  type="text"
                  name="lastname"
                  defaultValue={profile.lastName}
                  disabled
                />
              </label>
            </div>

            <div>
              <label>
                EMAIL
                <input
                  className="email-input"
                  type="text"
                  name="email"
                  defaultValue={profile.email}
                  disabled
                />
              </label>
            </div>

            <div className="row">
              <label className={formData.errors.company ? 'required' : ''}>
                COMPANY {formData.errors.company ? ' (REQUIRED)' : ''}
                <input
                  name="company"
                  type="text"
                  placeholder="Company"
                  className="add-info"
                  defaultValue={profile.companyName}
                  onChange={handleChange}
                />
              </label>

              <label className={formData.errors.jobtitle ? 'required' : ''}>
                JOB TITLE {formData.errors.jobtitle ? ' (REQUIRED)' : ''}
                <input
                  name="jobtitle"
                  type="text"
                  placeholder="Job title"
                  className="add-info"
                  defaultValue={profile.jobTitle}
                  onChange={handleChange}
                />
              </label>
            </div>
            <div className="row">
              <label>
                PHONE NUMBER
                <input
                  name="phone"
                  type="number"
                  className="add-info"
                  defaultValue={profile.phoneNumber}
                  onChange={handleChange}
                />
              </label>
              <label>
                <button
                  className="save-changes"
                  disabled={!saveChanges}
                  onClick={editProfile}
                >
                  Save Changes
                </button>
              </label>
            </div>

            {messageSent ? (
              <div className="message-sent">
                <p>We appreciate you contacting us.</p>
                <p>Our team will be contacting you soon.</p>
                <p className="wishing">Have a great day ahead!</p>
              </div>
            ) : (
              <div className="hubspot-container">
                <div className="text-message">
                  <label className={formData.errors.message ? 'required' : ''}>
                    Have something to say? Drop us a line{' '}
                    {formData.errors.message ? ' (REQUIRED)' : ''}
                    <textarea
                      name="message"
                      onChange={handleChange}
                      placeholder="Hello SoftServians! It was a fascinating demonstration. I really enjoyed attending this event. I was wondering – could you support with a project? Perhaps we can talk about it together next week?"
                    />
                  </label>
                </div>
                <div className="row updates-container">
                  <input
                    type="checkbox"
                    id="updates_and_offers"
                    name="updates_and_offers"
                    onChange={handleChange}
                  />
                  <label
                    htmlFor="updates_and_offers"
                    className="updates-checkbox"
                  >
                    I would like to be updated on latest products, event
                    announcements, and thought leadership.
                  </label>
                </div>
                <button type="submit">Send message</button>
                <p className="privacy-policy">
                  See our{' '}
                  <Link to="/privacy-notice">Privacy Notice</Link>{' '}
                  and{' '}
                  <a
                    href="https://www.softserveinc.com/en-us/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms and Conditions
                  </a>{' '}
                  to know how we use your data.
                </p>
              </div>
            )}
          </form>
        </div>
      )}
      <div className="profile-image">
        {profile.image ? (
          <ProfileCard onClick={onAvatarClick} card={profileCard} />
        ) : (
          <div className="nft-container" onClick={onAvatarClick}>
            <div className="avatar-card-header"></div>
            <div className="empty-nft">
              <p className="empty-title">THERE’S NOTHING HERE...</p>
              <p>
                This is where your NFT appears when you’ve been through the Know
                Your Future with Alnair demo.
              </p>
              <p>
                When you’ve experienced the demo, please use the QR code on the
                printed card to complete your profile and access your NFT.
              </p>
              <p>
                If you have not yet experienced the demo or your QR code isn’t
                working, please <NavLink to="/contact-us">contact us.</NavLink>
              </p>
            </div>
          </div>
        )}

        {/* <p className="click-text">Click on the card to open it</p> */}
      </div>
    </div>
  );
}

export default UserProfile;
